/* global Tangram */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getProject } from '../services/webService';
import { translate } from '../utils/translations';
import './Maps.css';

function Maps() {
  const { id } = useParams();
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const [projectSelected, setProjectSelected] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let project;
    if (!id) {
      project = getProject('gran_canaria_offline_map');
    } else {
      project = getProject(id);
    }

    if (!project) {
      navigate('/home');
      return;
    }

    setProjectSelected(project);
  }, [id, navigate]);

  useEffect(() => {
    if (!projectSelected || !mapRef.current) return;

    let mapInstance = null;
    let layer = null;

    async function initMap() {
      try {
        // Cargar Leaflet desde CDN
        const L = await loadLeaflet();

        // Cargar Tangram desde local
        await loadTangram();

        // Crear el mapa
        const map_start_location = [27.9582, -15.5965, 11]; // gran canaria

        // Parsear URL hash
        const url_hash = window.location.hash.slice(1).split('/');
        let startLocation = [...map_start_location];
        if (url_hash.length === 3) {
          startLocation = [parseFloat(url_hash[1]), parseFloat(url_hash[2]), parseFloat(url_hash[0])];
        }

        mapInstance = L.map(mapRef.current, {
          keyboardZoomOffset: 0.05
        });

        // Pasar la URL del scene.yaml a Tangram (él lo cargará)
        layer = Tangram.leafletLayer({
          scene: '/assets/maps/scene.yaml',
          attribution: '<a href="https://mapzen.com/tangram" target="_blank">Tangram</a> | &copy; OSM contributors | <a href="https://mapzen.com/" target="_blank">Mapzen</a>'
        });

        mapInstance.setView([startLocation[0], startLocation[1]], startLocation[2]);

        // Añadir capa al mapa
        layer.addTo(mapInstance);

      } catch (err) {
        console.error('Error initializing map:', err);
        setLoadError(true);
      }
    }

    initMap();

    return () => {
      if (mapInstance) {
        mapInstance.remove();
      }
    };
  }, [projectSelected]);

  if (!projectSelected) {
    return <div className="container-custom"><p>Loading...</p></div>;
  }

  return (
    <div className="container-custom">
      <br />
      <div className="row">
        <div className="col-lg-12">
          <ol className="breadcrumb">
            <li><Link to="/home">Home</Link></li>
            &nbsp;/&nbsp;
            <li className="active">
              <Link to={`/project/${projectSelected.id}`}>{translate(projectSelected.name)}</Link>
            </li>
          </ol>
        </div>
      </div>
      {loadError ? (
        <div className="alert alert-warning" role="alert">
          <h4 className="alert-heading">Map not available</h4>
          <p>The map could not be loaded. This may be due to missing dependencies (Tangram/Leaflet).</p>
          <hr />
          <p className="mb-0">
            <a href={`http://www.cloudsapps.net/projects/maps/${projectSelected.id}/index.html`}
              target="_blank" rel="noopener noreferrer"
              className="btn btn-primary">
              Open map in new tab
            </a>
          </p>
        </div>
      ) : (
        <div ref={mapRef} className="map-container"></div>
      )}
    </div>
  );
}

// Cargar Leaflet dinámicamente
function loadLeaflet() {
  return new Promise((resolve, reject) => {
    if (window.L) {
      resolve(window.L);
      return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://unpkg.com/leaflet@1.0.1/dist/leaflet.css';
    document.head.appendChild(link);

    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.0.1/dist/leaflet.js';
    script.onload = () => resolve(window.L);
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

// Cargar Tangram desde local
function loadTangram() {
  return new Promise((resolve, reject) => {
    if (window.Tangram) {
      resolve(window.Tangram);
      return;
    }
    const script = document.createElement('script');
    script.src = '/assets/lib/tangram.debug.js';
    script.onload = () => resolve(window.Tangram);
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

export default Maps;
